import { type ReactNode, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CloseIcon } from "vega-icons";

import { usePagination } from "@vega-api/custom/utils/usePagination";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import {
  getGetDealProspectsPermissionViewQueryKey,
  getGetLimitedPartnerUsersPermissionViewQueryKey,
  getGetLimitedPartnerUsersPermissionViewV2QueryKey,
  useAssignDealLimitedPartnerUsersRoles,
  useGetLimitedPartnerUsersPermissionView,
  useGetLimitedPartnerUsersPermissionViewV2,
  useRevokeDealLimitedPartnerUsersRoles,
  useUnlockShortlistItemPermissions,
  useUpdateDealLimitedPartnerUsersRoles,
} from "@vega-api/generated/coinvest-deal-permission/coinvest-deal-permission";
import { getGetDealShortlistQueryKey } from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import { type DealPermissionUser } from "@vega-api/generated/types/dealPermissionUser";
import { Avatar } from "@vega-core/components/Avatar/Avatar";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import {
  SearchUser,
  type SearchUserItem,
} from "@vega-core/components/Form/Controls/Input/SearchUser/SearchUser";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Notice, NoticeType } from "@vega-core/components/Notice/Notice";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { DotVariant, Tag, TagVariants } from "@vega-core/components/Tag/Tag";
import { useToast } from "@vega-core/components/Toast/useToast";
import { usePaginationState } from "@vega-core/hooks/usePaginationState";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { richTextFormat } from "@vega-core/utils/i18nHelpers";

import { MIN_SEARCH_QUERY_LENGTH } from "../../constants/searchConstants";
import { DEFAULT_DEAL_ROLE_NAME, useDealRoles } from "../../hooks/useDealRoles";
import { useUserSearch } from "../InviteUsersModal/hooks/useUserSearch";
import { type ProspectWithMemberCount } from "../OutreachTable/hooks/useOutreachManagement";

type RoleChange = {
  userId: string;
  newRole: string;
  roleId: string;
};

type NewUser = {
  user: SearchUserItem;
  roleId: string;
  roleName: string;
};

type ManageAccessStepProps = {
  dealId: string;
  prospect: ProspectWithMemberCount;
  onClose: () => void;
  onEmailOpportunity: () => void;
};

export const ManageAccessStep = ({
  dealId,
  prospect,
  onClose,
  onEmailOpportunity,
}: ManageAccessStepProps) => {
  const limitedPartnerId = prospect.limitedPartner.id;
  const showToast = useToast();
  const queryClient = useQueryClient();
  const { featureFlag } = useFeatureFlag();
  const isOutreachEmailEnabled =
    featureFlag[FeatureFlag.COINVEST_OUTREACH_EMAIL_VD_27885];

  const [pendingRoleChanges, setPendingRoleChanges] = useState<
    Map<string, RoleChange>
  >(new Map());
  const [pendingRemovals, setPendingRemovals] = useState<Set<string>>(
    new Set(),
  );
  const [newUsers, setNewUsers] = useState<NewUser[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: usersData,
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = useGetLimitedPartnerUsersPermissionView(dealId, limitedPartnerId, {
    query: { enabled: !!dealId && !!limitedPartnerId },
  });

  const lpUsers = usersData?.data?.users ?? [];
  const visibleUsers = lpUsers.filter((user) => !pendingRemovals.has(user.id));

  const { roleOptions, defaultRoleId, getRoleById, getRoleByName } =
    useDealRoles();

  const newUserIds = new Set(newUsers.map((u) => u.user.id));
  const allKnownUserIds = new Set([...lpUsers.map((u) => u.id), ...newUserIds]);

  const lpUsersPagination = usePaginationState(1, 5);

  const {
    data: lpUsersData,
    isLoading: isLpUsersLoading,
    isFetching: isLpUsersFetching,
  } = useGetLimitedPartnerUsersPermissionViewV2(
    dealId,
    limitedPartnerId,
    {
      hasPermissions: false,
      page: lpUsersPagination.currentPage,
      pageSize: lpUsersPagination.currentPageSize,
    },
    {
      query: { enabled: !!dealId && !!limitedPartnerId },
    },
  );

  const lpUsersWithoutAccess = lpUsersData?.data?.users ?? [];
  const pendingRemovalUsers =
    pendingRemovals.size > 0
      ? lpUsers.filter((u) => pendingRemovals.has(u.id))
      : [];

  const { paginationElement: lpUsersPaginationElement } = usePagination({
    meta: lpUsersData?.meta,
    isLoading: isLpUsersLoading,
    pageSizeOptions: [5, 10, 20],
    ...lpUsersPagination,
  });

  const {
    setSearchValue,
    users: searchResults,
    hasSearchableQuery,
    isPending: isSearchPending,
  } = useUserSearch({
    dealId,
    limitedPartnerId,
    selectedUserIds: allKnownUserIds,
  });

  const hasChanges =
    pendingRoleChanges.size > 0 ||
    pendingRemovals.size > 0 ||
    newUsers.length > 0;

  const totalVisibleUsers = visibleUsers.length + newUsers.length;

  const setPendingRoleChange = (
    userId: string,
    roleChange: RoleChange | null,
  ) => {
    setPendingRoleChanges((prev) => {
      const next = new Map(prev);

      if (roleChange) {
        next.set(userId, roleChange);
      } else {
        next.delete(userId);
      }

      return next;
    });
  };

  const clearPendingRoleChange = (userId: string) => {
    setPendingRoleChange(userId, null);
  };

  const setPendingRemoval = (userId: string, shouldRemove: boolean) => {
    setPendingRemovals((prev) => {
      const next = new Set(prev);

      if (shouldRemove) {
        next.add(userId);
      } else {
        next.delete(userId);
      }

      return next;
    });
  };

  const handleRoleChange = (user: DealPermissionUser, option: OptionType) => {
    if (option.value === user.role) {
      clearPendingRoleChange(user.id);

      return;
    }

    const selectedRole = getRoleByName(option.value);

    if (!selectedRole) return;

    setPendingRoleChange(user.id, {
      userId: user.id,
      newRole: selectedRole.name,
      roleId: selectedRole.id,
    });
  };

  const handleMarkForRemoval = (userId: string) => {
    setPendingRemoval(userId, true);
    clearPendingRoleChange(userId);
  };

  const cancelRemoval = (userId: string) => {
    setPendingRemoval(userId, false);
  };

  const handleAddLpUser = (user: DealPermissionUser) => {
    if (pendingRemovals.has(user.id)) {
      cancelRemoval(user.id);

      return;
    }

    const nameParts = user.fullName.split(" ");
    const firstName = nameParts[0] ?? "";
    const lastName = nameParts.slice(1).join(" ");

    const searchUserItem: SearchUserItem = {
      id: user.id,
      searchValue: `${user.fullName} ${user.email}`,
      content: null,
      user: { id: user.id, firstName, lastName, email: user.email },
    };

    handleAddUser(searchUserItem);
  };

  const handleAddUser = (user: SearchUserItem | null) => {
    if (!user) return;

    const defaultRole = getRoleById(defaultRoleId);

    setNewUsers((prev) => [
      ...prev,
      {
        user,
        roleId: defaultRoleId,
        roleName: defaultRole?.name ?? DEFAULT_DEAL_ROLE_NAME,
      },
    ]);
    setSearchValue("");
  };

  const handleRemoveNewUser = (userId: string) => {
    setNewUsers((prev) => prev.filter((u) => u.user.id !== userId));
  };

  const handleNewUserRoleChange = (userId: string, option: OptionType) => {
    const selectedRole = getRoleByName(option.value);

    if (!selectedRole) return;

    setNewUsers((prev) =>
      prev.map((u) =>
        u.user.id === userId
          ? { ...u, roleId: selectedRole.id, roleName: selectedRole.name }
          : u,
      ),
    );
  };

  const { mutateAsync: updateRoles } = useUpdateDealLimitedPartnerUsersRoles();
  const { mutateAsync: revokeRoles } = useRevokeDealLimitedPartnerUsersRoles();
  const { mutateAsync: assignRoles } = useAssignDealLimitedPartnerUsersRoles();
  const { mutateAsync: unlockPermissions } =
    useUnlockShortlistItemPermissions();

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: getGetLimitedPartnerUsersPermissionViewQueryKey(
        dealId,
        limitedPartnerId,
      ),
    });
    queryClient.invalidateQueries({
      queryKey: getGetLimitedPartnerUsersPermissionViewV2QueryKey(
        dealId,
        limitedPartnerId,
      ),
    });
    queryClient.invalidateQueries({
      queryKey: getGetDealShortlistQueryKey(dealId),
    });
    queryClient.invalidateQueries({
      queryKey: getGetDealProspectsPermissionViewQueryKey(dealId),
    });
  };

  const persistChanges = async () => {
    setIsSaving(true);

    try {
      const roleChanges = Array.from(pendingRoleChanges.values());

      await Promise.all([
        roleChanges.length > 0
          ? updateRoles({
              dealId,
              limitedPartnerId,
              data: {
                users: roleChanges.map((change) => ({
                  userId: change.userId,
                  roleId: change.roleId,
                })),
              },
            })
          : undefined,
        pendingRemovals.size > 0
          ? revokeRoles({
              dealId,
              limitedPartnerId,
              data: { userIds: Array.from(pendingRemovals) },
            })
          : undefined,
        newUsers.length > 0
          ? assignRoles({
              dealId,
              limitedPartnerId,
              data: {
                users: newUsers.map((u) => ({
                  userId: u.user.id,
                  roleId: u.roleId,
                })),
              },
            })
          : undefined,
      ]);

      // TODO(BE): If assign-roles auto-unlocks permissions, remove this separate unlock call
      if (prospect.isPermissionLocked) {
        await unlockPermissions({ dealId, shortlistItemId: prospect.id });
      }

      invalidateQueries();
      setPendingRoleChanges(new Map());
      setPendingRemovals(new Set());
      setNewUsers([]);

      return true;
    } catch {
      showToast({
        title: m.status_error(),
        variant: "destructive",
      });

      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const savePendingChanges = async () => {
    if (!hasChanges) return true;

    const success = await persistChanges();

    if (success) {
      showToast({
        title: m.coInvestments_share_accessUpdated(),
        variant: "success",
      });
    }

    return success;
  };

  const handleSave = async () => {
    if (!hasChanges) {
      onClose();

      return;
    }

    if (await savePendingChanges()) {
      onClose();
    }
  };

  const handleEmailOpportunity = async () => {
    const success = await savePendingChanges();

    if (!success) return;

    onEmailOpportunity();
  };

  const getDisplayedRole = (user: DealPermissionUser) => {
    const change = pendingRoleChanges.get(user.id);

    return change ? change.newRole : user.role;
  };

  if (isUsersError) {
    return (
      <ActionaryModal
        ModalButtonsGroupProps={{
          mainAction: onClose,
          mainActionText: m.btn_close(),
        }}
        open
        setOpen={onClose}
        title={m.coInvestments_share_title({
          lpName: prospect.limitedPartner.name,
        })}
      >
        <p className="body-base-regular text-text/negative">
          {m.errors_generic()}
        </p>
      </ActionaryModal>
    );
  }

  const renderUserRow = (
    userId: string,
    fullName: string,
    email: string,
    roleValue: string,
    onRoleChange: (option: OptionType) => void,
    onRemove: () => void,
  ) => (
    <div
      className="flex items-center gap-2 py-1"
      data-testid={`share-user-${userId}`}
      key={userId}
    >
      <Avatar initials={fullName} />
      <div className="flex flex-col min-w-0 grow">
        <span className="body-small-medium text-text/primary truncate">
          {fullName}
        </span>
        <span className="body-small-regular text-text/secondary truncate">
          {email}
        </span>
      </div>
      <ThemedDropdown
        className="w-max shrink-0"
        isClearable={false}
        name={`role-${userId}`}
        onChange={(option) => {
          if (option) onRoleChange(option);
        }}
        options={roleOptions}
        value={roleValue}
      />
      <ThemedButton
        className="shrink-0"
        dataTestId={`remove-user-${userId}`}
        icon={<CloseIcon className="w-4 h-4" />}
        onClick={onRemove}
        variant="text"
      />
    </div>
  );

  const renderLpUserRow = ({
    user,
    actionLabel,
    onClick,
    disabled,
    nameAdornment,
  }: {
    user: DealPermissionUser;
    actionLabel: ReactNode;
    onClick: () => void;
    disabled?: boolean;
    nameAdornment?: ReactNode;
  }) => (
    <div
      className="flex items-center gap-2 py-1"
      data-testid={`lp-user-${user.id}`}
      key={user.id}
    >
      <Avatar initials={user.fullName} />
      <div className="flex flex-col min-w-0 grow">
        {nameAdornment ? (
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="body-small-medium text-text/primary truncate">
              {user.fullName}
            </span>
            {nameAdornment}
          </div>
        ) : (
          <span className="body-small-medium text-text/primary truncate">
            {user.fullName}
          </span>
        )}
        <span className="body-small-regular text-text/secondary truncate">
          {user.email}
        </span>
      </div>
      <ThemedButton
        className="shrink-0 min-h-7 h-8 px-3"
        dataTestId={`add-lp-user-${user.id}`}
        disabled={disabled}
        onClick={onClick}
        size="small"
        variant="secondary"
      >
        {actionLabel}
      </ThemedButton>
    </div>
  );

  const cancelButton = (
    <ThemedButton
      dataTestId="share-modal-cancel"
      disabled={isSaving}
      onClick={onClose}
      variant="secondary"
    >
      {m.btn_cancel()}
    </ThemedButton>
  );

  const modalButtonsGroupProps = isOutreachEmailEnabled
    ? {
        mainAction: handleSave,
        mainActionText: m.btn_save(),
        disabled: !hasChanges || isSaving,
        thirdActionElement: cancelButton,
      }
    : {
        mainAction: handleEmailOpportunity,
        mainActionText: m.coInvestments_share_saveAndContinue(),
        disabled: isSaving || totalVisibleUsers === 0,
        secondaryAction: handleSave,
        secondaryActionText: m.btn_save(),
        disabledSecondary: !hasChanges || isSaving,
        thirdActionElement: cancelButton,
      };

  return (
    <ActionaryModal
      ModalButtonsGroupProps={modalButtonsGroupProps}
      dataTestId="share-modal-manage-access"
      description={m.coInvestments_share_manageAccessDescription()}
      fluidWidth
      open
      setOpen={onClose}
      title={
        isOutreachEmailEnabled
          ? m.coInvestments_share_manageAccessTitle({
              lpName: prospect.limitedPartner.name,
            })
          : m.coInvestments_share_title({
              lpName: prospect.limitedPartner.name,
            })
      }
    >
      <div className="space-y-4 text-left w-dvw max-w-2xl">
        <SearchUser
          clearOnSelect
          emptyMessage={
            hasSearchableQuery
              ? undefined
              : m.common_searchMinCharsHint({ count: MIN_SEARCH_QUERY_LENGTH })
          }
          isLoading={isSearchPending}
          name="shareSearchUser"
          noPadding
          onChange={setSearchValue}
          onUserSelect={handleAddUser}
          placeholder={m.coInvestments_permissions_searchUsersPlaceholder()}
          users={searchResults}
        />

        {isUsersLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }, (_, i) => (
              <div className="flex items-center gap-2" key={i}>
                <Skeleton className="size-8 rounded-full shrink-0" />
                <div className="flex flex-col gap-1 grow">
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-8 w-36 shrink-0" />
              </div>
            ))}
          </div>
        ) : (
          <div>
            {newUsers.length > 0 ? (
              <div className="space-y-1">
                <span className="body-small-medium text-text/secondary">
                  {m.coInvestments_share_addingAccess()}
                </span>
                {newUsers.map((newUser) =>
                  renderUserRow(
                    newUser.user.id,
                    `${newUser.user.user.firstName} ${newUser.user.user.lastName}`,
                    newUser.user.user.email,
                    newUser.roleName,
                    (option) =>
                      handleNewUserRoleChange(newUser.user.id, option),
                    () => handleRemoveNewUser(newUser.user.id),
                  ),
                )}
              </div>
            ) : null}

            {visibleUsers.length > 0 ? (
              <div className="space-y-1">
                <span className="body-small-medium text-text/secondary">
                  {m.coInvestments_share_whoHasAccess()}
                </span>
                {visibleUsers.map((user) =>
                  renderUserRow(
                    user.id,
                    user.fullName,
                    user.email,
                    getDisplayedRole(user),
                    (option) => handleRoleChange(user, option),
                    () => handleMarkForRemoval(user.id),
                  ),
                )}
              </div>
            ) : null}

            {newUsers.length > 0 || visibleUsers.length > 0 ? (
              <Notice
                className="mt-2"
                content={
                  <span className="whitespace-pre-line body-small-regular">
                    {richTextFormat(
                      m.coInvestments_gpManageRolesDescription(),
                      {
                        bold: ({ children }) => (
                          <span className="font-semibold">{children}</span>
                        ),
                      },
                    )}
                  </span>
                }
                dataTestId="gp-manage-roles-description"
                expandable={false}
                type={NoticeType.DEFAULT}
              />
            ) : null}

            {totalVisibleUsers === 0 ? (
              <p className="body-base-regular text-text/secondary py-4 text-center">
                {m.coInvestments_contacts_modal_empty()}
              </p>
            ) : null}

            <div className="mt-4 border-t border-border/subtle pt-4">
              <span className="body-small-medium text-text/secondary">
                {m.coInvestments_share_lpUsers({
                  lpName: prospect.limitedPartner.name,
                })}
              </span>
              <div className="max-h-80 overflow-y-auto pr-2">
                {pendingRemovalUsers.map((user) =>
                  renderLpUserRow({
                    user,
                    actionLabel: m.btn_undo(),
                    onClick: () => handleAddLpUser(user),
                    nameAdornment: (
                      <Tag
                        dotVariant={DotVariant.REMOVED}
                        variant={TagVariants.Warning}
                      >
                        {m.coInvestments_share_removingAccess()}
                      </Tag>
                    ),
                  }),
                )}

                {pendingRemovalUsers.length > 0 &&
                lpUsersWithoutAccess.length > 0 ? (
                  <div className="border-t border-border/subtle my-1" />
                ) : null}

                {!isLpUsersFetching &&
                lpUsersWithoutAccess.length === 0 &&
                pendingRemovalUsers.length === 0 ? (
                  <p className="body-base-regular text-text/secondary py-4 text-center">
                    {m.coInvestments_share_noLpUsers()}
                  </p>
                ) : null}
                {isLpUsersFetching && lpUsersWithoutAccess.length === 0
                  ? Array.from({ length: 5 }, (_, i) => (
                      <div className="flex items-center gap-2 py-1" key={i}>
                        <Skeleton className="size-8 rounded-full shrink-0" />
                        <div className="flex flex-col gap-1 grow">
                          <Skeleton className="h-3.5 w-32" />
                          <Skeleton className="h-3 w-48" />
                        </div>
                        <Skeleton className="h-8 w-16 shrink-0" />
                      </div>
                    ))
                  : lpUsersWithoutAccess.map((user) => {
                      const isAdded = newUserIds.has(user.id);
                      const actionLabel = isAdded
                        ? `+ ${m.coInvestments_share_userAdded()}`
                        : `+ ${m.coInvestments_share_addUser()}`;

                      return renderLpUserRow({
                        user,
                        actionLabel,
                        onClick: () => handleAddLpUser(user),
                        disabled: isAdded,
                      });
                    })}
              </div>
              <div className="mt-3">{lpUsersPaginationElement}</div>
            </div>
          </div>
        )}
      </div>
    </ActionaryModal>
  );
};
